/**
 * RequestDetails Component
 * Displays full details of an LLM request with formatted JSON
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Divider,
  Alert
} from '@mui/material';
import { format } from 'date-fns';
import { LLMRequest } from '../../services/llm-monitoring';

interface RequestDetailsProps {
  request: LLMRequest;
}

const RequestDetails: React.FC<RequestDetailsProps> = ({ request }) => {
  const formatJSON = (data: any) => {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  return (
    <Box>
      {/* Request Info */}
      <Typography variant="h6" gutterBottom>
        Request Information
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">Timestamp</Typography>
          <Typography variant="body2">
            {format(new Date(request.timestamp), 'PPpp')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">User ID</Typography>
          <Typography variant="body2">{request.user_id}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">Session ID</Typography>
          <Typography variant="body2">{request.session_id || 'N/A'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="caption" color="text.secondary">Request Type</Typography>
          <Chip label={request.request_type} size="small" color="primary" />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Model Info */}
      <Typography variant="h6" gutterBottom>
        Model Information
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="caption" color="text.secondary">Model</Typography>
          <Typography variant="body2">{request.model}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="caption" color="text.secondary">Provider</Typography>
          <Typography variant="body2">{request.provider_used || 'Default'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="caption" color="text.secondary">Fallback Used</Typography>
          <Chip 
            label={request.fallback_used ? 'Yes' : 'No'} 
            size="small" 
            color={request.fallback_used ? 'warning' : 'default'}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Performance Metrics */}
      <Typography variant="h6" gutterBottom>
        Performance Metrics
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary">Prompt Tokens</Typography>
          <Typography variant="h6">{request.prompt_tokens}</Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary">Completion Tokens</Typography>
          <Typography variant="h6">{request.completion_tokens}</Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary">Total Tokens</Typography>
          <Typography variant="h6">{request.total_tokens}</Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary">Cost</Typography>
          <Typography variant="h6">${Number(request.cost).toFixed(6)}</Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary">Latency</Typography>
          <Typography variant="h6">{request.latency_ms}ms</Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary">Cache Hit</Typography>
          <Chip 
            label={request.cache_hit ? 'Yes' : 'No'} 
            size="small" 
            color={request.cache_hit ? 'success' : 'default'}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary">Status</Typography>
          <Chip 
            label={request.success ? 'Success' : 'Failed'} 
            size="small" 
            color={request.success ? 'success' : 'error'}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Typography variant="caption" color="text.secondary">Finish Reason</Typography>
          <Typography variant="body2">{request.finish_reason || 'N/A'}</Typography>
        </Grid>
      </Grid>

      {/* Tool Information */}
      {request.tool_name && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Tool Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">Tool Name</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {request.tool_name}
              </Typography>
            </Grid>
            {request.confidence !== undefined && request.confidence !== null && (
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Confidence</Typography>
                <Typography variant="body2">
                  {(Number(request.confidence) * 100).toFixed(1)}%
                </Typography>
              </Grid>
            )}
            {request.reasoning && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Reasoning</Typography>
                <Typography variant="body2">{request.reasoning}</Typography>
              </Grid>
            )}
            {request.tool_parameters && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Parameters</Typography>
                <Paper sx={{ p: 1, bgcolor: 'grey.50', mt: 1 }}>
                  <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace' }}>
                    {formatJSON(request.tool_parameters)}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </>
      )}

      {/* Error Information */}
      {!request.success && request.error_message && (
        <>
          <Divider sx={{ my: 2 }} />
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Error Message</Typography>
            <Typography variant="body2">{request.error_message}</Typography>
          </Alert>
        </>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Request Messages */}
      <Typography variant="h6" gutterBottom>
        Request Messages
      </Typography>
      <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 3, maxHeight: 400, overflow: 'auto' }}>
        <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
          {formatJSON(request.request_messages)}
        </Typography>
      </Paper>

      {/* Response Content */}
      <Typography variant="h6" gutterBottom>
        Response Content
      </Typography>
      <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 400, overflow: 'auto' }}>
        <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
          {formatJSON(request.response_content)}
        </Typography>
      </Paper>
    </Box>
  );
};

export default RequestDetails;